import { Box, Button, ButtonBase, Icon, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import C from '@shared/constants/general';
import { AuthContext, ConferenceContext, PusherContext } from '@/store';
import { infoList, sortAsTree } from '@/lib/utils/DataFormat';
import { MarkdownRender } from '@/components/markdown/markdownRender';
import { DiscussionConnection } from './DiscussionConnection';
import { DiscussionMessageEdit } from './DiscussionMessageEdit';
import { DiscussionTagSomeone } from './DiscussionTagSomeone';
import { DiscussionVisitor } from './DiscussionVisitor';

const messageStyles = (theme) => ({
  smallReply: {
    marginLeft: '-5px !important',
    fontSize: '0.65rem !important',
    opacity: '0.3 !important',
    color: 'rgba(83, 80, 80, 0.87) !important',
    '&:hover': {
      opacity: '1 !important',
      color: 'rgba(0, 0, 0, 0.87) !important',
      backgroundColor: 'rgba(185, 184, 184, 0.7) !important',
    },
  },
  timeAgo: {
    paddingLeft: '8px !important',
    fontSize: '0.75rem !important',
    opacity: '0.5 !important',
    fontStyle: 'italic !important',
  },
  container: {
    paddingTop: '16px !important',
    position: 'relative !important',
  },
  user: {
    opacity: '0.3 !important',
    '&:hover': {
      opacity: '1 !important',
    },
  },

  userName: {
    textDecoration: 'underline !important',
  },
  avatarHolder: {
    padding: '0 8px 2px 0 !important',
  },
  text: {
    // whiteSpace: 'pre-wrap',
    fontSize: '1.1rem !important',
    backgroundColor: `${theme.palette.background.shadedown_10} !important`,
    borderRadius: '5px !important',
    padding: '1px 8px !important',
    marginRight: '5px !important',
  },
  userNameStar: {
    fontSize: '12px !important',
    width: '10px !important',
    height: '10px !important',
  },
  pin: {
    position: 'absolute !important',
    opacity: '0.6 !important',
    top: '14px !important',
    right: '0px !important',
    // backgroundColor: theme.palette.background.default,
    padding: '5px !important',
    '& > *': {
      fontSize: '1rem !important',
    },
  },
});

const MessageDetail = React.memo(
  (props) => {
    const theme = useTheme();
    const classes = messageStyles(theme);
    const { authState } = useContext(AuthContext);
    const { conferenceState, openUserPopup } = useContext(ConferenceContext);
    const LEFT_PADDING = 16;

    const fromNow = moment
      .utc(props.message.updatedAt)
      .utcOffset(-conferenceState.conferenceDetail.utc_offset)
      .fromNow();

    const senderIsContributer =
      props.message.visitor &&
      props.message.visitor.participation_level ===
        C.VISITOR.PARTICIPATION_LEVEL.CONTRIBUTE;

    const isModerator =
      authState.user &&
      authState.user.access_level === C.VISITOR.ACCESS_LEVEL.MODERATOR;

    return (
      <Box
        sx={classes.container}
        style={{ paddingLeft: props.message.depth * LEFT_PADDING }}
      >
        <Box>
          {props.message.visitor ? (
            <ButtonBase
              sx={classes.user}
              onClick={() => openUserPopup(props.message.visitor)}
            >
              <Box display="flex" alignItems="center">
                {/*<Box className={classes.avatarHolder}>
                    <UserAvatar size="small" user={props.message.visitor} />
            </Box>*/}
                <Box>
                  <Box component="span" sx={classes.userName}>
                    {props.message.visitor.full_name}
                  </Box>
                  {senderIsContributer && (
                    <Icon color="primary" sx={classes.userNameStar}>
                      star
                    </Icon>
                  )}
                  {', '}
                  {infoList([
                    props.message.visitor.company,
                    props.message.visitor.location_city,
                  ])}
                </Box>
              </Box>
            </ButtonBase>
          ) : props.message.text ? (
            <Box sx={classes.user}>Participant no longer exist</Box>
          ) : (
            <Box sx={classes.user}>Message removed</Box>
          )}
          <Box component="span" sx={classes.timeAgo}>
            {fromNow}
          </Box>
          {props.message.edited && (
            <Box component="span" sx={classes.timeAgo}>
              (edited)
            </Box>
          )}
        </Box>
        <Box sx={classes.text}>
          <MarkdownRender text={props.message.text} />
        </Box>
        {props.message.pinned > 0 && (
          <Box sx={classes.pin}>
            <Icon>push_pin</Icon>
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Box>
            <Button
              onClick={() =>
                props.reply(props.message, {
                  discussionId: props.message.discussionId,
                  parentDiscussionMessageId: props.message.id,
                  text: '',
                  pinned: 0,
                })
              }
              startIcon={<Icon>reply</Icon>}
              size="small"
              sx={classes.smallReply}
            >
              Reply
            </Button>
            {(authState.user.id === props.message.visitorId || isModerator) && (
              <Button
                onClick={() => props.reply(null, props.message)}
                startIcon={<Icon>edit</Icon>}
                size="small"
                sx={classes.smallReply}
              >
                Edit
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => prevProps.updatedAt === nextProps.updatedAt
);
MessageDetail.displayName = 'MessageDetail';

const useStyles = (theme, { alwaysSmall }) => ({
  holder: {
    alignItems: 'flex-start !important',
    [`${theme.breakpoints.down('sm')}`]: {
      flexDirection: 'column !important',
    },
    ...(alwaysSmall
      ? {
          flexDirection: 'column !important',
        }
      : null),
  },
  text: {
    // whiteSpace: 'pre-wrap',
    fontSize: '1.1rem !important',
  },
  discussionHolder: {
    borderLeftColor: `${theme.palette.background.shadedown_20} !important`,
    borderLeftStyle: 'solid !important',
    borderLeftWidth: '1px !important',
    width: '100% !important',
  },
  title: {
    fontWeight: `${theme.typography.fontWeightBold} !important`,
    fontSize: '1.25rem !important',
    lineHeight: '1.2rem !important',
    paddingBottom: '8px !important',
  },
  tagButton: {
    opacity: '0.5 !important',
    '&:hover': {
      opacity: '1 !important',
    },
  },
  timeAgo: {
    paddingRight: '8px !important',
    fontSize: '0.75rem !important',
    opacity: '0.5 !important',
    fontStyle: 'italic !important',
  },
  btnStyle: {
    color: 'rgba(0, 0, 0, 0.87) !important',
    '&:hover': {
      backgroundColor: 'rgba(173, 173, 173, 0.7) !important',
      opacity: '1 !important',
    },
  },
});

// const sortAsTree = (list, treeList, parentDiscussionMessageId, depth) => {
//   const messagesOnThesDepth = list.filter(
//     (m) => m.parentDiscussionMessageId === parentDiscussionMessageId
//   );
//   messagesOnThesDepth.forEach((m) => {
//     const messageData = {
//       ...m,
//       depth: depth,
//     };
//     treeList.push(messageData);
//     sortAsTree(list, treeList, m.id, depth + 1);
//   });
//   return treeList;
// };

/**
 * @typedef {{ discussionId: string, isEmbedded?: boolean, alwaysSmall?: boolean }} DiscussionProps
 */

export const Discussion = React.memo(
  /** @param {DiscussionProps} props */
  ({ discussionId, isEmbedded = false, alwaysSmall = false }) => {
    const editRef = useRef();
    const tagRef = useRef();
    const theme = useTheme();
    const classes = useStyles(theme, { alwaysSmall });
    const { authState } = useContext(AuthContext); // Hämta Context
    const { conferenceState, openNewDiscussion } = useContext(
      ConferenceContext
    );
    const [discussion, setDiscussion] = useState(null);
    const isModerator =
      authState.user.access_level === C.VISITOR.ACCESS_LEVEL.MODERATOR;
    const { pusher, ROOM_TYPE, actions: pusherActions } = useContext(
      PusherContext
    ); // Hämta Context

    useEffect(() => {
      if (discussionId) {
        axios
          .get(`/api/visitor/discussions/${discussionId}`)
          .then((response) => {
            setAndReSort(response.data);
          });
      }
    }, []);

    const setAndReSort = (discussionData) => {
      setDiscussion({
        ...discussionData,
        messageTree: sortAsTree(
          discussionData.discussion_messages,
          [],
          null,
          0
        ),
      });
    };

    const addMessageAndReSort = (msg) => {
      setDiscussion((currentData) => {
        if (
          msg.visitorId === authState.user.id &&
          msg.parentDiscussionMessageId === null
        ) {
          // If replying to root yourself, you have probably started following the discussion
          currentData.discussion_followers[0] = {
            visitorId: authState.user.id,
            discussionId: currentData.id,
          }; // Just dummy data (never used)
        }
        currentData.discussion_messages.unshift(msg);
        return {
          ...currentData,
          messageTree: sortAsTree(currentData.discussion_messages, [], null, 0),
        };
      });
    };

    const toggleFollow = () => {
      if (discussion.discussion_followers.length === 0) {
        axios
          .post(`/api/visitor/discussions/${discussionId}/follow`)
          .then((response) => {
            const updated = { ...discussion };
            updated.discussion_followers[0] = response.data;
            setDiscussion(updated);
          });
      } else {
        axios
          .delete(`/api/visitor/discussions/${discussionId}/follow`)
          .then(() => {
            const updated = { ...discussion };
            updated.discussion_followers = [];
            setDiscussion(updated);
          });
      }
    };

    const updateMessage = (msg) => {
      setDiscussion((currentData) => {
        const updatedIndex = currentData.discussion_messages.findIndex(
          (dm) => dm.id === msg.id
        );
        if (updatedIndex >= 0) {
          currentData.discussion_messages[updatedIndex] = msg;
          return {
            ...currentData,
            messageTree: sortAsTree(
              currentData.discussion_messages,
              [],
              null,
              0
            ),
          };
        } else {
          return currentData;
        }
      });
    };
    const deleteMessage = (msg) => {
      setDiscussion((currentData) => {
        const deletedIndex = currentData.discussion_messages.findIndex(
          (dm) => dm.id === msg.id
        );
        if (deletedIndex >= 0) {
          currentData.discussion_messages.splice(deletedIndex, 1);
          return {
            ...currentData,
            messageTree: sortAsTree(
              currentData.discussion_messages,
              [],
              null,
              0
            ),
          };
        } else {
          return currentData;
        }
      });
    };

    const tagSomeone = () => {
      tagRef.current.open({
        discussionId,
      });
    };

    const reply = (parentMessage, message) => {
      editRef.current.open({
        discussion,
        parentMessage,
        message,
      });
    };

    const editDiscussion = () => {
      // setEditDiscussions({ title: '', url: '', text: '' });
      openNewDiscussion({
        discussion: discussion,
        callback: (updatedDiscussion) => {
          if (updatedDiscussion) {
            setDiscussion((currentData) => {
              return {
                ...currentData,
                ...updatedDiscussion,
              };
            });
          } else {
            setDiscussion(null); // Deleted
          }
        },
      });
    };

    useEffect(() => {
      if (pusher.current) {
        const entityChannel = pusherActions.subscribe(
          ROOM_TYPE.DISCUSSION,
          discussionId
        );
        entityChannel.bind('new-response', (data) => {
          addMessageAndReSort(data);
        });
        entityChannel.bind('update-response', (data) => {
          updateMessage(data);
        });
        entityChannel.bind('delete-response', (data) => {
          deleteMessage(data);
        });
      }
      return () => {
        if (pusher.current) {
          pusherActions.unSubscribe(ROOM_TYPE.DISCUSSION, discussionId);
        }
      };
    }, [!!pusher.current, discussionId]);

    const fromNow =
      discussion &&
      moment
        .utc(discussion.createdAt)
        .utcOffset(-conferenceState.conferenceDetail.utc_offset)
        .fromNow();

    let connectionData;
    if (discussion && discussion.connection) {
      if (discussion.connection_type === 'entity') {
        connectionData = {
          picture:
            discussion.connection.picture || discussion.connection.bg_picture,
          title: discussion.connection.title,
          url: `/live/item/${discussion.connection.id}`,
        };
      } else if (discussion.connection_type === 'event') {
        connectionData = {
          picture: '', // TODO: Add video img after video is finished
          title: discussion.connection.title,
          url: `/live/session/${discussion.connection.id}`,
        };
      }
    }
    return discussion ? (
      <Box>
        <Box display="flex" alignItems="flext-start" sx={classes.holder}>
          <DiscussionVisitor
            visitor={discussion.visitor}
            alwaysSmall={alwaysSmall}
          />
          <Box flexGrow={1} sx={classes.discussionHolder}>
            <Box p={1}>
              {!isEmbedded && connectionData && (
                <Box pb={1}>
                  <DiscussionConnection
                    type={discussion.connection_type}
                    picture={connectionData.picture}
                    title={connectionData.title}
                    url={connectionData.url}
                  />
                </Box>
              )}

              <Typography variant="body2" sx={classes.title}>
                {discussion.title}
              </Typography>

              {discussion.url && (
                <Box>
                  <Link href={discussion.url} target="_blank">
                    {discussion.url}
                  </Link>
                </Box>
              )}
              {discussion.text && (
                <Box sx={classes.text}>
                  <MarkdownRender text={discussion.text} />
                </Box>
              )}
              <Box sx={classes.timeAgo}>
                {fromNow} {discussion.edited && '(edited)'}
              </Box>

              <Box>
                <Button
                  onClick={() =>
                    reply(null, {
                      discussionId: discussion.id,
                      text: '',
                      pinned: 0,
                    })
                  }
                  color="primary"
                  startIcon={<Icon>reply</Icon>}
                  size="small"
                >
                  Comment
                </Button>
                <Button
                  onClick={toggleFollow}
                  color={
                    discussion.discussion_followers.length === 0
                      ? 'primary'
                      : 'secondary'
                  }
                  startIcon={
                    <Icon>
                      {discussion.discussion_followers.length === 0
                        ? 'bookmark_add'
                        : 'bookmark_remove'}
                    </Icon>
                  }
                  size="small"
                >
                  {discussion.discussion_followers.length === 0
                    ? 'Follow'
                    : 'Unfollow'}
                </Button>
                {(discussion.visitorId === authState.user.id ||
                  isModerator) && (
                  <Button
                    onClick={editDiscussion}
                    startIcon={<Icon>edit</Icon>}
                    size="small"
                    sx={classes.btnStyle}
                  >
                    edit
                  </Button>
                )}
                <Button
                  sx={{ ...classes.tagButton, ...classes.btnStyle }}
                  onClick={tagSomeone}
                  startIcon={<Icon>person_add</Icon>}
                  size="small"
                >
                  Notify someone
                </Button>
              </Box>
            </Box>
            <Box pl={1}>
              {discussion.discussion_messages.length === 0 ? (
                <Box>Be first to reply</Box>
              ) : (
                <Box>
                  {discussion.messageTree.map((message) => (
                    <MessageDetail
                      message={message}
                      key={message.id}
                      reply={reply}
                      updatedAt={message.updatedAt}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <DiscussionMessageEdit ref={editRef} />
        <DiscussionTagSomeone ref={tagRef} />
      </Box>
    ) : (
      <Box align="center" p={2}>
        ...
      </Box>
    );
  },
  /** @param {DiscussionProps} prevProps @param {DiscussionProps} nextProps */
  (prevProps, nextProps) => prevProps.discussionId === nextProps.discussionId
);

Discussion.displayName = 'Discussion';
